import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Button,
  Divider,
  Paper,
} from "@mui/material";
import {
  RedesignrChip,
  WfIconMiscCalendarDateRegular,
  WfIconNavigationChevronDownRegular,
} from "@WahooFitness/redesignr";
import HistoricalFitnessProfile4DP from "./HistoricalFitnessProfile4DP";
import { t, Trans } from "@lingui/macro";
import { FitnessProfileAlgorithm } from "@WahooFitness/cloud-client-types";
import { useMemo } from "react";
import formatDate from "@/services/formatDate";
import HistoricalFitnessProfileThrCad from "./HistoricalFitnessProfileThrCad";
import { HistoricalProfile } from "./useHistoricalFitnessProfiles";

interface Props {
  profile: HistoricalProfile;
  expanded: boolean;
  onChange: () => void;
  handleViewWorkoutDetails: (assessmentId: number) => void;
}

function DateHeader({ date }: { date: string }) {
  return (
    <Box display="flex" alignItems="center" gap={0.5} pb={0.5}>
      <WfIconMiscCalendarDateRegular fontSize="small" />
      <Typography variant="prose-sm-medium">{formatDate({ date, format: "PPP" })}</Typography>
    </Box>
  );
}

function CurrentProfileChip() {
  return <RedesignrChip readOnly size="small" color="info" label={t`Current profile`} />;
}

export const HistoricalFitnessProfileCard = ({
  profile,
  expanded,
  onChange,
  handleViewWorkoutDetails,
}: Props) => {
  const fitnessProfileName = useMemo(() => {
    switch (profile.fitness_profile_algorithm_id) {
      case FitnessProfileAlgorithm.FullFrontalAssessment:
        return "Full Frontal";
      case FitnessProfileAlgorithm.HalfMontyAssessment:
        return "Half Monty";
      case FitnessProfileAlgorithm.FourDPQuiz:
        return t`Questionnaire`;
      case FitnessProfileAlgorithm.Custom:
        return t`Manual`;
      case FitnessProfileAlgorithm.SystmOnboarding:
        return t`SYSTM Onboarding`;
      default:
        return "";
    }
  }, [profile.fitness_profile_algorithm_id]);

  const isFitnessTestWithoutAssessment = useMemo(() => {
    return (
      [
        FitnessProfileAlgorithm.FullFrontalAssessment,
        FitnessProfileAlgorithm.HalfMontyAssessment,
      ].includes(profile.fitness_profile_algorithm_id) && profile.assessment_id === null
    );
  }, [profile.assessment_id, profile.fitness_profile_algorithm_id]);

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      variant="outlined"
      component={Paper}
      sx={{ border: 0 }}
      disableGutters
    >
      <AccordionSummary expandIcon={<WfIconNavigationChevronDownRegular />} sx={{ px: 1.5 }}>
        <Box display="flex" flexDirection="column">
          {!isFitnessTestWithoutAssessment && <DateHeader date={profile.updated_at} />}
          {profile.assessment_id && (
            <Typography variant="prose-sm" color="text.secondary">
              <Trans>Fitness assessment</Trans>
            </Typography>
          )}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="prose-xl-medium">{fitnessProfileName}</Typography>
            {isFitnessTestWithoutAssessment && <CurrentProfileChip />}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0.5 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Divider />
          {profile && (
            <HistoricalFitnessProfile4DP
              ftp={profile.dim_1 ?? undefined}
              map={profile.dim_2 ?? undefined}
              ac={profile.dim_3 ?? undefined}
              nm={profile.dim_4 ?? undefined}
              previousFtp={profile.previousProfile?.dim_1 ?? undefined}
              previousMap={profile.previousProfile?.dim_2 ?? undefined}
              previousAc={profile.previousProfile?.dim_3 ?? undefined}
              previousNm={profile.previousProfile?.dim_4 ?? undefined}
            />
          )}
          <HistoricalFitnessProfileThrCad
            thr={profile.threshold_hr ?? undefined}
            cad={profile.preferred_cadence ?? undefined}
            previousThr={profile.previousProfile?.threshold_hr ?? undefined}
            previousCad={profile.previousProfile?.preferred_cadence ?? undefined}
          />
          {profile.assessment_id && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => handleViewWorkoutDetails(profile.assessment_id!)}
            >
              <Trans>View workout details</Trans>
            </Button>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
